import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import {
  getPracticeSetQuestions, getPracticeAttempt, recordPracticeAnswerInsights,
  type PracticeTestQuestion, type QuestionAnswer, type CompletedSegment, type UnderstandingSummary,
} from '../services/practiceTestService';
import type { PaperBlueprint } from '../config/regionConfig';
import { supabase } from '../services/supabaseClient';
import { getStoredSession } from '../hooks/useAuth';
import { buildProvenanceLabel } from '../utils/provenanceLabel';
import { Trophy, Clock, CheckCircle2, XCircle, RotateCcw, BookOpen, Sparkles, AlertTriangle, ArrowRight, Loader2, Layers } from 'lucide-react';
import ChinniExplainer, { type ExplainResult } from '../components/ChinniExplainer';

const OPTION_LABELS = ['A', 'B', 'C', 'D'];

interface ResultsState {
  score: number;
  total: number;
  timeTaken: number;
  answers: QuestionAnswer[];
  questions: PracticeTestQuestion[];
  marksObtained?: number;
  maxMarks?: number;
  sectionScores?: Record<string, { obtained: number; max: number }>;
  blueprint?: PaperBlueprint;
  examType?: string;
  subject?: string;
  isRetake?: boolean;
  /** Present for continuous-flow exams (SAT, JEE Main/Advanced, NEET) — one entry per subject. */
  sessionResults?: CompletedSegment[];
}

interface ExamFeedback {
  grade: string;
  summary: string;
  strengths: string[];
  focus_areas: string[];
  next_steps: string;
  motivational_note: string;
}

// ── FeedbackCard ───────────────────────────────────────────────

function FeedbackCard({ feedback, loading }: { feedback: ExamFeedback | null; loading: boolean }) {
  if (loading) {
    return (
      <div className="glass-card rounded-xl p-5 shadow-sm mb-6 animate-fade-in-up">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-coral-warm animate-pulse" />
          <span className="text-sm font-bold text-ocean-deep">AI Feedback</span>
        </div>
        <div className="space-y-2">
          <div className="h-3 bg-gray-100 rounded-full animate-pulse w-3/4" />
          <div className="h-3 bg-gray-100 rounded-full animate-pulse w-1/2" />
          <div className="h-3 bg-gray-100 rounded-full animate-pulse w-5/6" />
        </div>
        <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" />
          Generating personalised feedback…
        </p>
      </div>
    );
  }

  if (!feedback) return null;

  return (
    <div className="glass-card rounded-xl p-5 shadow-sm mb-6 animate-fade-in-up">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-coral-warm" />
          <span className="text-sm font-bold text-ocean-deep">AI Feedback</span>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
          feedback.grade === 'Excellent'
            ? 'bg-green-100 text-green-700'
            : feedback.grade === 'Good'
              ? 'bg-amber-100 text-amber-700'
              : 'bg-red-100 text-red-600'
        }`}>
          {feedback.grade}
        </span>
      </div>

      <p className="text-sm text-gray-700 leading-relaxed mb-4">{feedback.summary}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div className="bg-green-50 rounded-lg p-3 border border-green-100">
          <div className="flex items-center gap-1.5 mb-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
            <span className="text-xs font-bold text-green-700">Strengths</span>
          </div>
          <ul className="space-y-1">
            {feedback.strengths.map((s, i) => (
              <li key={i} className="text-xs text-green-800 flex items-start gap-1.5">
                <span className="mt-0.5 text-green-500">▸</span>{s}
              </li>
            ))}
          </ul>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-xs font-bold text-amber-700">Focus Areas</span>
          </div>
          <ul className="space-y-1">
            {feedback.focus_areas.map((f, i) => (
              <li key={i} className="text-xs text-amber-800 flex items-start gap-1.5">
                <span className="mt-0.5 text-amber-500">▸</span>{f}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bg-orange-50 rounded-lg p-3 border border-orange-100 mb-3">
        <div className="flex items-center gap-1.5 mb-1">
          <ArrowRight className="w-3.5 h-3.5 text-coral-warm" />
          <span className="text-xs font-bold text-coral-warm">What to do next</span>
        </div>
        <p className="text-xs text-orange-800">{feedback.next_steps}</p>
      </div>

      <p className="text-xs text-gray-500 italic text-center">{feedback.motivational_note}</p>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────

export default function PracticeTestResults() {
  const location = useLocation();
  const { attemptId } = useParams<{ attemptId: string }>();
  const routeState = location.state as ResultsState | null;
  const storedSession = getStoredSession();
  const gradeLevel = storedSession?.grade_level ?? 12;
  const studentId = storedSession?.student_id;

  const [loadedState, setLoadedState] = useState<ResultsState | null>(routeState);
  const [stateLoading, setStateLoading] = useState(!routeState);

  useEffect(() => {
    if (routeState || !attemptId || attemptId === '0' || !studentId) return;

    async function loadFromDb() {
      setStateLoading(true);
      try {
        const attempt = await getPracticeAttempt(parseInt(attemptId!), studentId!);

        if (!attempt) return;

        const questions = await getPracticeSetQuestions(attempt.set_id);
        setLoadedState({
          score: attempt.score,
          total: attempt.total_questions,
          timeTaken: attempt.time_taken_seconds ?? 0,
          answers: (attempt.answers as QuestionAnswer[]) ?? [],
          questions,
          marksObtained: attempt.marks_obtained ?? undefined,
          maxMarks: attempt.max_marks ?? undefined,
          sectionScores: attempt.section_scores ?? undefined,
          isRetake: attempt.is_retake,
        });
      } finally {
        setStateLoading(false);
      }
    }

    loadFromDb();
  }, [attemptId, routeState, studentId]);

  const state = loadedState;

  const [feedback, setFeedback] = useState<ExamFeedback | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);

  useEffect(() => {
    if (!attemptId || attemptId === '0' || !state || !studentId) return;

    async function loadOrGenerateFeedback() {
      const cached = await getPracticeAttempt(parseInt(attemptId!), studentId!);

      if (cached?.ai_feedback) {
        setFeedback(cached.ai_feedback as ExamFeedback);
        return;
      }

      setFeedbackLoading(true);
      try {
        const marksObtained = state!.marksObtained ?? state!.score;
        const maxMarks = state!.maxMarks ?? state!.total;
        const percentage = maxMarks > 0 ? Math.round((marksObtained / maxMarks) * 100) : 0;

        const res = await supabase.functions.invoke('generate-exam-feedback', {
          body: {
            attempt_id: parseInt(attemptId!),
            exam_type: state!.examType ?? 'Exam',
            subject: state!.subject ?? 'General',
            marks_obtained: marksObtained,
            max_marks: maxMarks,
            percentage,
            section_scores: state!.sectionScores ?? null,
          },
        });

        if (res.data?.feedback) {
          setFeedback(res.data.feedback as ExamFeedback);
        }
      } catch (err) {
        console.error('Failed to load feedback:', err);
      } finally {
        setFeedbackLoading(false);
      }
    }

    loadOrGenerateFeedback();
  }, [attemptId, state, studentId]);

  // Per-question explanations (ChinniExplainer) auto-fetch on mount and
  // report back here via onInsight; once every question on this attempt has
  // resolved, persist the enriched answers + an attempt-level understanding
  // rollup so the parent overview can read it (record_practice_answer_insights,
  // migration 10069). Guarded by insightsPersistedRef so it only fires once.
  const [insights, setInsights] = useState<Map<number, ExplainResult>>(new Map());
  const insightsPersistedRef = useRef(false);

  const handleInsight = useCallback((questionId: number, result: ExplainResult) => {
    setInsights((prev) => {
      const next = new Map(prev);
      next.set(questionId, result);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!state || insightsPersistedRef.current) return;
    if (state.questions.length === 0 || insights.size < state.questions.length) return;
    if (!attemptId || attemptId === '0' || !studentId) return;
    insightsPersistedRef.current = true;

    const answersByIdForPersist = new Map<number, QuestionAnswer>();
    for (const a of state.answers) answersByIdForPersist.set(a.question_id, a);

    const enrichedAnswers: QuestionAnswer[] = state.questions.map((q) => {
      const existing = answersByIdForPersist.get(q.question_id);
      const insight = insights.get(q.question_id);
      return {
        question_id: q.question_id,
        question_type: q.question_type,
        selected: existing?.selected ?? null,
        selected_options: existing?.selected_options,
        numerical_answer: existing?.numerical_answer ?? null,
        text_answer: existing?.text_answer,
        is_correct: existing?.is_correct ?? null,
        marks_awarded: existing?.marks_awarded ?? 0,
        time_spent_ms: existing?.time_spent_ms ?? 0,
        steps: insight?.steps,
        common_mistake: insight?.common_mistake,
        ui_status: insight?.ui_status,
        ui_message: insight?.ui_message,
        detailed_model_answer: insight?.detailed_model_answer,
      };
    });

    const summary: UnderstandingSummary = { solid: 0, mostly_there: 0, needs_review: 0, ungraded: 0 };
    for (const q of state.questions) {
      const insight = insights.get(q.question_id);
      if (!insight) summary.ungraded++;
      else summary[insight.ui_status]++;
    }

    recordPracticeAnswerInsights(parseInt(attemptId), studentId, enrichedAnswers, summary);
  }, [state, insights, attemptId, studentId]);

  if (stateLoading) {
    return (
      <main className="max-w-2xl mx-auto px-4 py-8 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-coral-warm mx-auto mb-4" />
        <p className="text-gray-500 text-sm">Loading results…</p>
      </main>
    );
  }

  if (!state) {
    return (
      <main className="max-w-2xl mx-auto px-4 py-8 text-center">
        <p className="text-gray-600 mb-4">No results data available.</p>
        <Link to="/practice-test" className="text-coral-warm font-medium hover:underline">
          Go to Practice Tests
        </Link>
      </main>
    );
  }

  const { score, total, timeTaken, answers, questions, marksObtained, maxMarks, sectionScores, sessionResults } = state;
  const hasMarks = maxMarks != null && maxMarks > 0;
  const marksPercent = hasMarks ? Math.round(((marksObtained || 0) / maxMarks!) * 100) : Math.round((score / total) * 100);
  const hasSubjective = questions.some(q => q.question_type === 'short_answer' || q.question_type === 'long_answer');

  const answersById = new Map<number, QuestionAnswer>();
  for (const a of answers) answersById.set(a.question_id, a);

  function formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  }

  function formatMs(ms: number): string {
    const s = Math.round(ms / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
  }

  const scoreColor = marksPercent >= 80 ? 'text-green-600' : marksPercent >= 50 ? 'text-amber-600' : 'text-red-600';
  const scoreBg = marksPercent >= 80 ? 'from-green-500 to-emerald-600' : marksPercent >= 50 ? 'from-amber-400 to-orange-500' : 'from-red-500 to-rose-600';

  const sections: { label: string; questions: PracticeTestQuestion[] }[] = [];
  let curLabel = '';
  for (const q of questions) {
    const label = q.section_label || '';
    if (label !== curLabel || sections.length === 0) {
      sections.push({ label, questions: [] });
      curLabel = label;
    }
    sections[sections.length - 1].questions.push(q);
  }

  return (
    <main className="max-w-2xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8">
      {/* Score card */}
      <div className="glass-card rounded-xl p-6 sm:p-8 shadow-lg text-center mb-6 animate-fade-in-up">
        <div className={`w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${scoreBg} flex items-center justify-center shadow-lg`}>
          <Trophy className="w-10 h-10 text-white" />
        </div>

        {hasMarks ? (
          <>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-ocean-deep mb-1">
              {marksObtained}/{maxMarks} marks
            </h1>
            <p className={`text-2xl font-bold ${scoreColor} mb-1`}>{marksPercent}%</p>
            {hasSubjective && (
              <p className="text-xs text-gray-400 mb-3">Auto-graded portion only. Compare subjective answers with model answers below.</p>
            )}
          </>
        ) : (
          <>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-ocean-deep mb-1">
              {score}/{total}
            </h1>
            <p className={`text-2xl font-bold ${scoreColor} mb-3`}>{marksPercent}%</p>
          </>
        )}

        <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {formatTime(timeTaken)}
          </div>
          <div className="flex items-center gap-1">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            {score} correct
          </div>
          <div className="flex items-center gap-1">
            <XCircle className="w-4 h-4 text-red-500" />
            {answers.filter(a => a.is_correct === false).length} wrong
          </div>
          {answers.filter(a => a.is_correct === null).length > 0 && (
            <div className="flex items-center gap-1">
              <BookOpen className="w-4 h-4 text-blue-500" />
              {answers.filter(a => a.is_correct === null).length} self-check
            </div>
          )}
        </div>
      </div>

      {/* Session summary — continuous-flow exams (SAT, JEE Main/Advanced, NEET) covered every subject in one sitting */}
      {sessionResults && sessionResults.length > 1 && (
        <div className="glass-card rounded-xl p-5 shadow-sm mb-6 animate-fade-in-up">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-4 h-4 text-coral-warm" />
            <span className="text-sm font-bold text-ocean-deep">Session Summary — {sessionResults.length} subjects</span>
          </div>
          <div className="space-y-2">
            {sessionResults.map((seg) => {
              const pct = seg.maxMarks > 0 ? Math.round((seg.marksObtained / seg.maxMarks) * 100) : 0;
              return (
                <div key={seg.subject} className="flex items-center gap-3">
                  <span className="text-xs font-medium text-gray-700 w-32 flex-shrink-0 truncate">{seg.subject}</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-500'}`}
                      style={{ width: `${Math.max(pct, 2)}%` }}
                    />
                  </div>
                  <span className="text-xs font-bold text-gray-600 w-24 text-right">{seg.marksObtained}/{seg.maxMarks}m ({pct}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AI Feedback Card */}
      <FeedbackCard feedback={feedback} loading={feedbackLoading} />

      {/* Section-wise summary */}
      {sectionScores && Object.keys(sectionScores).length > 1 && (
        <div className="glass-card rounded-xl p-5 shadow-sm mb-6 animate-fade-in-up">
          <h2 className="text-sm font-bold text-ocean-deep mb-3">Section Scores</h2>
          <div className="space-y-2">
            {Object.entries(sectionScores).map(([label, scores]) => {
              const pct = scores.max > 0 ? Math.round((scores.obtained / scores.max) * 100) : 0;
              return (
                <div key={label} className="flex items-center gap-3">
                  <span className="text-xs font-medium text-gray-700 w-24 flex-shrink-0">{label || 'Default'}</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-amber-400' : 'bg-red-500'}`}
                      style={{ width: `${Math.max(pct, 2)}%` }}
                    />
                  </div>
                  <span className="text-xs font-bold text-gray-600 w-20 text-right">{scores.obtained}/{scores.max}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Question breakdown by section */}
      {sections.map((section, sIdx) => (
        <div key={sIdx} className="mb-6">
          {section.label && (
            <h2 className="text-base font-bold text-ocean-deep mb-3 animate-fade-in-up">{section.label}</h2>
          )}
          {!section.label && sections.length === 1 && (
            <h2 className="text-lg font-bold text-ocean-deep mb-3 animate-fade-in-up">Question Breakdown</h2>
          )}
          {sIdx === 0 && (
            <p className="text-xs text-gray-400 mb-3">Step-by-step explanations are available for every question below.</p>
          )}
          <div className="space-y-3">
            {section.questions.map((q) => {
              const answer = answersById.get(q.question_id);
              return (
                <QuestionResult
                  key={q.question_id} question={q} answer={answer}
                  index={questions.indexOf(q)} formatMs={formatMs}
                  gradeLevel={gradeLevel}
                  conceptContext={`${state?.examType ?? 'Exam'} ${state?.subject ?? ''}`}
                  examType={state?.examType}
                  stateBoard={storedSession?.state_board}
                  onInsight={handleInsight}
                />
              );
            })}
          </div>
        </div>
      ))}

      {/* Action buttons */}
      <div className="flex gap-3 animate-fade-in-up">
        <Link
          to="/practice-test"
          className="flex-1 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded-xl font-bold text-sm shadow-lg hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          {state.isRetake ? 'Back to Exams' : 'Try Another Exam'}
        </Link>
        <Link
          to="/practice-test/archive"
          className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2"
        >
          <BookOpen className="w-4 h-4" />
          My Past Papers
        </Link>
      </div>
    </main>
  );
}

function QuestionResult({ question: q, answer, index, formatMs, gradeLevel, conceptContext, examType, stateBoard, onInsight }: {
  question: PracticeTestQuestion;
  answer?: QuestionAnswer;
  index: number;
  formatMs: (ms: number) => string;
  gradeLevel: number;
  conceptContext: string;
  examType?: string;
  stateBoard?: string;
  onInsight: (questionId: number, result: ExplainResult) => void;
}) {
  const type = q.question_type || 'mcq_single';
  const options = [q.option_a, q.option_b, q.option_c, q.option_d];

  const borderColor = answer?.is_correct === true
    ? 'border-l-green-500'
    : answer?.is_correct === false
      ? 'border-l-red-500'
      : answer?.is_correct === null
        ? 'border-l-blue-400'
        : 'border-l-gray-300';

  return (
    <div className={`glass-card rounded-xl p-4 shadow-sm border-l-4 animate-fade-in-up ${borderColor}`}>
      <div className="text-[11px] font-medium text-gray-400 mb-1">
        {buildProvenanceLabel({
          provenance: q.provenance,
          exam_type: examType,
          state_board: stateBoard,
          year_level: gradeLevel,
          source_year: q.source_year,
          source_session: q.source_session,
          source_question_number: q.source_question_number,
          marks: q.marks,
        })}
      </div>
      <div className="flex items-start justify-between gap-3 mb-2">
        <p className="text-sm font-medium text-ocean-deep flex-1">
          <span className="text-gray-400 mr-1">Q{index + 1}.</span>
          {q.question}
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          {answer && <span className="text-xs text-gray-400">{formatMs(answer.time_spent_ms)}</span>}
          <span className="text-xs font-medium text-gray-500">{answer?.marks_awarded ?? 0}/{q.marks}m</span>
        </div>
      </div>

      {(type === 'mcq_single' || type === 'assertion_reasoning') && (
        <div className="flex flex-wrap gap-2 text-xs">
          {answer?.selected != null ? (
            <span className={`px-2 py-1 rounded-lg font-medium ${answer.is_correct ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              Your answer: {OPTION_LABELS[answer.selected]} — {options[answer.selected]}
            </span>
          ) : (
            <span className="px-2 py-1 rounded-lg bg-gray-50 text-gray-500 font-medium">Skipped</span>
          )}
          {answer?.is_correct === false && q.correct_option_index != null && (
            <span className="px-2 py-1 rounded-lg bg-green-50 text-green-700 font-medium">
              Correct: {OPTION_LABELS[q.correct_option_index]} — {options[q.correct_option_index]}
            </span>
          )}
        </div>
      )}
      {(type === 'mcq_single' || type === 'assertion_reasoning') && q.correct_option_index != null && (
        <ChinniExplainer
          question={q.question}
          questionType={type}
          optionA={q.option_a ?? undefined} optionB={q.option_b ?? undefined}
          optionC={q.option_c ?? undefined} optionD={q.option_d ?? undefined}
          correctIndex={q.correct_option_index}
          assertionText={q.assertion_text ?? undefined}
          reasonText={q.reason_text ?? undefined}
          studentSelected={answer?.selected ?? undefined}
          conceptName={conceptContext}
          gradeLevel={gradeLevel}
          autoFetch
          onResult={(r) => onInsight(q.question_id, r)}
        />
      )}

      {type === 'mcq_multi' && (
        <div className="flex flex-wrap gap-2 text-xs">
          {(answer?.selected_options?.length ?? 0) > 0 ? (
            <span className={`px-2 py-1 rounded-lg font-medium ${answer?.is_correct ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              Your: {answer!.selected_options!.map(i => OPTION_LABELS[i]).join(', ')}
            </span>
          ) : (
            <span className="px-2 py-1 rounded-lg bg-gray-50 text-gray-500 font-medium">Skipped</span>
          )}
          {answer?.is_correct === false && q.correct_options && (
            <span className="px-2 py-1 rounded-lg bg-green-50 text-green-700 font-medium">
              Correct: {q.correct_options.map(i => OPTION_LABELS[i]).join(', ')}
            </span>
          )}
        </div>
      )}
      {type === 'mcq_multi' && q.correct_options && (
        <ChinniExplainer
          question={q.question}
          questionType={type}
          optionA={q.option_a ?? undefined} optionB={q.option_b ?? undefined}
          optionC={q.option_c ?? undefined} optionD={q.option_d ?? undefined}
          correctOptions={q.correct_options}
          studentSelectedOptions={answer?.selected_options}
          conceptName={conceptContext}
          gradeLevel={gradeLevel}
          autoFetch
          onResult={(r) => onInsight(q.question_id, r)}
        />
      )}

      {type === 'numerical' && (
        <div className="flex flex-wrap gap-2 text-xs">
          {answer?.numerical_answer != null ? (
            <span className={`px-2 py-1 rounded-lg font-medium ${answer.is_correct ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              Your answer: {answer.numerical_answer}
            </span>
          ) : (
            <span className="px-2 py-1 rounded-lg bg-gray-50 text-gray-500 font-medium">Skipped</span>
          )}
          {answer?.is_correct === false && q.correct_answer_numerical != null && (
            <span className="px-2 py-1 rounded-lg bg-green-50 text-green-700 font-medium">
              Correct: {q.correct_answer_numerical}
              {q.numerical_tolerance ? ` (±${q.numerical_tolerance})` : ''}
            </span>
          )}
        </div>
      )}
      {type === 'numerical' && q.correct_answer_numerical != null && (
        <ChinniExplainer
          question={q.question}
          questionType={type}
          correctAnswerNumerical={q.correct_answer_numerical}
          numericalTolerance={q.numerical_tolerance ?? undefined}
          studentNumericalAnswer={answer?.numerical_answer ?? undefined}
          conceptName={conceptContext}
          gradeLevel={gradeLevel}
          autoFetch
          onResult={(r) => onInsight(q.question_id, r)}
        />
      )}

      {(type === 'short_answer' || type === 'long_answer' || type === 'case_based') && (
        <div className="space-y-2 mt-2">
          {answer?.text_answer ? (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-[10px] font-semibold text-gray-500 mb-1">Your Answer</p>
              <p className="text-xs text-gray-800 whitespace-pre-wrap">{answer.text_answer}</p>
            </div>
          ) : (
            <span className="text-xs px-2 py-1 rounded-lg bg-gray-50 text-gray-500 font-medium inline-block">Skipped</span>
          )}
          {q.model_answer && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-[10px] font-semibold text-blue-600 mb-1">Not auto-graded — compare with the model answer below</p>
              <p className="text-xs text-blue-900 whitespace-pre-wrap">{q.model_answer}</p>
            </div>
          )}
          <ChinniExplainer
            question={q.question}
            questionType={type}
            modelAnswer={q.model_answer ?? undefined}
            caseStudyText={q.case_study_text ?? undefined}
            studentTextAnswer={answer?.text_answer}
            conceptName={conceptContext}
            gradeLevel={gradeLevel}
            autoFetch
            onResult={(r) => onInsight(q.question_id, r)}
          />
        </div>
      )}
    </div>
  );
}
